
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Chip,
  Paper,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";

// Icons
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { PortfolioGetFolderStructure } from "../../util/API/portfolio";

import BulkUploadDrawer from "./BulkUploadDrawer";

const FileExplorer = ({ onBack }) => {
  const configVariables = useSelector((state) => state.config?.baseConfig);
  const userSecurityRoles = JSON.parse(sessionStorage.getItem("user"))?.payload
    ?.body?.Securityuserrole;
  const userId = JSON.parse(sessionStorage.getItem("user"))?.userid || "";

  const userRole = useMemo(() => {
    const userRoles = Array.isArray(userSecurityRoles)
      ? userSecurityRoles
      : [userSecurityRoles];
    const parseIds = (ids) =>
      ids
        ?.toString()
        .split(",")
        .map((id) => Number(id.trim())) || [];

    const adminRoles = parseIds(configVariables?.portfolioAdminRoleIds);
    const delegateRoles = parseIds(configVariables?.delegateRoleIds);

    if (userRoles.some((r) => adminRoles.includes(Number(r?.roleid))))
      return "admin";
    if (userRoles.some((r) => delegateRoles.includes(Number(r?.roleid))))
      return "delegate";
    return "investor";
  }, [userSecurityRoles, configVariables]);

  const [folderTree, setFolderTree] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [openNodes, setOpenNodes] = useState({});
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

 const getFolderStructure = async () => {
  setIsLoading(true);
  const roleToBeSend = userRole === 'admin' ? 'admin' : 'investor';
  try {
    const response = await PortfolioGetFolderStructure(userId, roleToBeSend);
    const items = response?.data?.fileList?.item;
    if (!items) return;

    const itemsArray = Array.isArray(items) ? items : [items];

    // ── Build a nested map structure from s3Key paths ──
    // root = { [segmentName]: { __meta, [childSegment]: {...} } }
    const root = {};

    const getOrCreateFolder = (parentObj, segName, fullPath) => {
      if (!parentObj[segName]) {
        parentObj[segName] = {
          __meta: {
            id: fullPath,
            name: segName,
            type: 'folder',
            children: [],        // files only at this level
            __subfolders: {},    // nested folder nodes
          },
        };
      }
      return parentObj[segName];
    };

    itemsArray.forEach((item) => {
      const { s3Key, accessUrl, lastModified, fileSize } = item;
      if (!s3Key) return;

      const segments = s3Key.split('/').filter(Boolean);
      if (segments.length === 0) return;

      const lastSeg = segments[segments.length - 1];
      const isFile = lastSeg.includes('.');

      // folder segments = all segments except the last if it's a file
      const folderSegs = isFile ? segments.slice(0, -1) : segments;
      if (folderSegs.length === 0) return;

      // Walk down, creating folder nodes as needed
      let currentObj = root;
      let pathSoFar = '';

      folderSegs.forEach((seg) => {
        pathSoFar = pathSoFar ? `${pathSoFar}/${seg}` : seg;
        const node = getOrCreateFolder(currentObj, seg, pathSoFar);
        currentObj = node.__meta.__subfolders;
      });

      // Attach file to the deepest folder's children
      if (isFile) {
        // find the deepest folder's __meta
        let cur = root;
        let deepestMeta = null;
        folderSegs.forEach((seg) => {
          deepestMeta = cur[seg].__meta;
          cur = deepestMeta.__subfolders;
        });
        if (deepestMeta) {
          deepestMeta.children.push({
            id: s3Key,
            name: lastSeg,
            type: 'file',
            date: lastModified?.split(' ')[0],
            accessUrl: accessUrl?.replace(/&amp;/g, '&'),
            fileSize,
          });
        }
      }
    });

    // ── Convert nested map → tree array ──
    const buildTree = (obj) => {
      return Object.keys(obj)
        .filter((k) => k !== '__meta')
        .map((key) => {
          const meta = obj[key].__meta;
          const subfolderNodes = buildTree(meta.__subfolders);
          return {
            id: meta.id,
            name: meta.name,
            type: 'folder',
            // subfolders first, then files
            children: [...subfolderNodes, ...meta.children],
          };
        });
    };

    const tree = buildTree(root);

    const initialOpenNodes = {};
    tree.forEach((node) => { initialOpenNodes[node.id] = true; });
    setOpenNodes(initialOpenNodes);
    setFolderTree(tree);
  } catch (error) {
    console.log('getFolderStructure error:', error);
  } finally {
    setIsLoading(false);
  }
};

  useEffect(() => {
    if (userId && userRole) {
      getFolderStructure();
    }
  }, [userId, userRole]);

  // 4. DEFAULT SELECTION — pick first category once tree loads
  useEffect(() => {
    if (folderTree.length > 0 && !selectedFolder) {
      setSelectedFolder(folderTree[0]);
    }
  }, [folderTree]);

  const handleToggle = (id) => {
    setOpenNodes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isInsideProtectedBranch = (nodeId) => {
    const fundsBranch = folderTree.find((c) => c.id === "funds");
    if (!fundsBranch) return false;
    const findInTree = (root) => {
      if (root.id === nodeId) return true;
      if (root.children)
        return root.children.some((child) => findInTree(child));
      return false;
    };
    return findInTree(fundsBranch);
  };

  // 5. TREE COMPONENT
  const RenderTree = ({ node, level = 0 }) => {
    const isFolder = node.type === "folder";
    const isOpen = openNodes[node.id];

    return (
      <React.Fragment>
        <ListItemButton
          onClick={() => {
            if (isFolder) {
              handleToggle(node.id);
              setSelectedFolder(node);
            }
          }}
          selected={selectedFolder?.id === node.id}
          sx={{ pl: level * 2 + 2, py: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            {isFolder ? (
              isOpen ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )
            ) : (
              <Box sx={{ width: 20 }} />
            )}
            {isFolder ? (
              <FolderIcon
                fontSize="small"
                sx={{
                  color: node.id === "Global Repository" ? "#1976d2" : "#ffca28",
                  ml: 0.5,
                }}
              />
            ) : (
              <InsertDriveFileIcon
                fontSize="small"
                sx={{ color: "#90a4ae", ml: 0.5 }}
              />
            )}
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            primaryTypographyProps={{
              fontSize: "0.8rem",
              fontWeight: selectedFolder?.id === node.id ? 700 : 400,
              noWrap: true,
            }}
          />
        </ListItemButton>

        {isFolder && node.children && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children.map((child) => (
                <RenderTree key={child.id} node={child} level={level + 1} />
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid #eee",
          bgcolor: "#fff",
        }}
      >
        {/* ── Header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
          borderBottom="1px solid #eee"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={onBack} size="small">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="bold">
              Document Hub
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5}>
            <Button
              variant="contained"
              size="small"
              startIcon={
                <FileUploadIcon sx={{ fontSize: "1rem !important" }} />
              }
              onClick={() => setBulkUploadOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                bgcolor: "#002e4c",
                px: 2,
                py: 0.75,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#004a7a" },
              }}
            >
              Bulk Upload
            </Button>

            <Chip
              icon={
                userRole === "admin" ? (
                  <AdminPanelSettingsIcon
                    sx={{ fontSize: "1rem !important" }}
                  />
                ) : userRole === "delegate" ? (
                  <SupervisorAccountIcon sx={{ fontSize: "1rem !important" }} />
                ) : (
                  <PersonIcon sx={{ fontSize: "1rem !important" }} />
                )
              }
              label={userRole.toUpperCase()}
              color={
                userRole === "admin"
                  ? "primary"
                  : userRole === "delegate"
                    ? "secondary"
                    : "default"
              }
              size="small"
            />
          </Box>
        </Box>

        <Grid container sx={{ height: "75vh", overflow: 'hidden' }}>
          {/* ── Sidebar ── */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              borderRight: "1px solid #eee",
              bgcolor: "#fcfcfc",
              overflowY: "auto",
              maxHeight: '75vh'
            }}
          >
            <List disablePadding sx={{ py: 1 }}>
              {isLoading ? (
                <Box p={3} textAlign="center">
                  <Typography variant="caption" color="textSecondary">
                    Loading...
                  </Typography>
                </Box>
              ) : folderTree.length > 0 ? (
                folderTree.map((node) => (
                  <RenderTree key={node.id} node={node} />
                ))
              ) : (
                <Box p={3} textAlign="center">
                  <Typography variant="caption" color="textSecondary">
                    No documents found.
                  </Typography>
                </Box>
              )}
            </List>
          </Grid>

          {/* ── Content Area ── */}
          <Grid item xs={12} md={9} sx={{ p: 4, overflowY: "auto",  maxHeight: '75vh', }}>
            {selectedFolder ? (
              <>
                <Box mb={3}>
                  <Typography variant="h5" fontWeight="900" color="#002e4c">
                    {selectedFolder.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedFolder.children?.length || 0} item
                    {selectedFolder.children?.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>

                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <List disablePadding>
                    {selectedFolder.children?.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ListItemButton
                          onClick={() =>
                            item.type === "folder" && setSelectedFolder(item)
                          }
                          sx={{ py: 2 }}
                        >
                          <ListItemIcon>
                            {item.type === "folder" ? (
                              <FolderIcon sx={{ color: "#ffca28" }} />
                            ) : (
                              <InsertDriveFileIcon color="action" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            secondary={
                              item.type === "folder"
                                ? `${item.children?.length || 0} file${item.children?.length !== 1 ? "s" : ""}`
                                : `Added: ${item.date}`
                            }
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {item.type === "file" && (
                              <IconButton
                                size="small"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const response = await fetch(
                                      item.accessUrl,
                                    );
                                    const blob = await response.blob();
                                    const blobUrl = URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = blobUrl;
                                    link.download = item.name;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(blobUrl);
                                  } catch (err) {
                                    console.error("Download failed:", err);
                                    // fallback: open in new tab
                                    window.open(item.accessUrl, "_blank");
                                  }
                                }}
                              >
                                <FileDownloadIcon
                                  fontSize="small"
                                  color="primary"
                                />
                              </IconButton>
                            )}

                            {item.type === "file" &&
                              userRole !== "delegate" &&
                              (userRole === "admin" ||
                                !isInsideProtectedBranch(
                                  selectedFolder.id,
                                )) && (
                                <IconButton size="small">
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              )}
                          </Box>
                        </ListItemButton>
                        {index < selectedFolder.children.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}

                    {(!selectedFolder.children ||
                      selectedFolder.children.length === 0) && (
                      <Box p={6} textAlign="center">
                        <Typography color="textSecondary" variant="body2">
                          This folder is empty
                        </Typography>
                      </Box>
                    )}
                  </List>
                </Paper>
              </>
            ) : (
              <Box
                display="flex"
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                <Typography color="textSecondary">
                  Select a directory from the sidebar
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* ── Bulk Upload Drawer ── */}
      <BulkUploadDrawer
        userRole={userRole}
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onUploadComplete={() => {
          setBulkUploadOpen(false);
          // Re-fetch folder structure after upload
          setSelectedFolder(null);
          setFolderTree([]);
          getFolderStructure();
        }}
      />
    </>
  );
};

export default FileExplorer;


